import { formatVolume, formatWeight, roundTo, volumeReadsZero } from "./units.js";
import { formatKcal } from "./sessionSummaryMath.js";
import type { ShareCardEnergy, ShareCardLabels } from "./shareCard.js";

// The share card's private formatters, split out of shareCard.ts
// (SP-SHARECARD, which was 456 lines). All five were module-private and
// nothing outside shareCard.ts ever called them, so no external contract
// moves with them: this module is NOT added to the package barrel, and the
// package's public surface is byte-for-byte what it was.
//
// Of the original five, `formatDuration` and `buildFooterStats` kept an
// `export` keyword because shareCard.ts still calls them (a third,
// `formatFooter`, also had one until its only caller was deleted with
// RW-14, see below). formatTut and formatImpulseCompact stay private here:
// their only callers are their siblings below, so the split actually
// narrowed their visibility rather than widening it.
//
// The import of ShareCardEnergy/ShareCardLabels from shareCard.ts is
// deliberately `import type`. shareCard.ts imports these functions as
// VALUES, so a value-level back-import would be a real cycle; a type-only
// one is erased at compile time, leaving the emitted module graph
// one-directional (shareCard -> shareCardFormat) with no load-order
// hazard. The types stay in shareCard.ts because they are its public
// vocabulary — callers build the labels bundle and pass it in.

// Format minutes → "Xh Ym" with no decimals.
export const formatDuration = (
  minutes: number | null,
  labels: ShareCardLabels,
): string => {
  if (minutes === null || !Number.isFinite(minutes) || minutes <= 0) return "";
  const total = Math.round(minutes);
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m} ${labels.durationMinute}`;
  return `${h}${labels.durationHour} ${m}${labels.durationMinuteShort}`;
};

// Time under tension seconds → "m:ss" (or "h:mm:ss" past an hour).
const formatTut = (sec: number): string => {
  const total = Math.round(sec);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
};

// Impulse (kg·s) → compact "2.5k" / "850".
// Threshold on the rounded value (DS-35's `roundTo`, see units.ts), so
// 999.6 renders 1.0k instead of "1000".
const formatImpulseCompact = (kgS: number): string => {
  const rounded = roundTo(kgS, 0);
  return rounded >= 1000 ? `${(kgS / 1000).toFixed(1)}k` : `${rounded}`;
};

// `formatFooter`, the dense "12 sets · 84 min · 18.9k kg · 225 kg/min" line
// this module used to also export, had its only caller (shareCard.ts's
// `footerMetrics` field, kept for the legacy web renderer) removed with
// RW-14 on 2026-09-24 (recovery SHA 7ae8e35e); deleted here in the same pass.

// Structured footer STAT-STRIP (HC-8 B3). Labelled value-over-label cells,
// with Calories appended from the resolved energy when its provenance clears
// LEGAL-5 (see the guard below, no Effort cell exists at all, same reason).
// Each cell is pushed only when it carries information → a plain strength
// session yields exactly Volume/Duration/Density/[Calories]; a timed/
// isometric one also gets TUT/Impulse (no regression vs the dense line).
export const buildFooterStats = (
  durationMinutes: number | null,
  totalVolumeKg: number,
  densityKgPerMin: number | null,
  totalTutSec: number,
  totalImpulseKgS: number,
  energy: ShareCardEnergy | undefined,
  unit: "kg" | "lb",
  locale: string,
  labels: ShareCardLabels,
): Array<{ label: string; value: string }> => {
  const cells: Array<{ label: string; value: string }> = [];
  if (totalVolumeKg > 0 && !volumeReadsZero(totalVolumeKg, unit)) {
    cells.push({
      label: labels.volumeLabel,
      value: formatVolume(totalVolumeKg, unit, locale, labels.units),
    });
  }
  const dur = formatDuration(durationMinutes, labels);
  if (dur) cells.push({ label: labels.durationLabel, value: dur });
  if (densityKgPerMin !== null && densityKgPerMin > 0) {
    cells.push({
      label: labels.densityLabel,
      value: `${formatWeight(densityKgPerMin, unit, locale, labels.units)}${labels.footerDensitySuffix}`,
    });
  }
  if (totalTutSec > 0) {
    cells.push({ label: labels.tutLabel, value: formatTut(totalTutSec) });
  }
  if (totalImpulseKgS > 0) {
    cells.push({
      label: labels.impulseLabel,
      value: `${formatImpulseCompact(totalImpulseKgS)} ${labels.impulseUnit}`,
    });
  }
  // LEGAL-5 (2026-09-09): the card is the artifact that LEAVES the app, so no
  // cell on it may carry a value derived from Health Connect. Heart rate has
  // exactly one source — READ_HEART_RATE via Health Connect (manual HR entry
  // was excluded, MSC-3) — so there is no such thing as an Effort cell that
  // isn't HC data, and the cell is gone, full stop, not just gated.
  // Calories has a genuinely ours-only path instead, so it's RIPIEGATO rather
  // than dropped (titolare, 2026-09-09): `kcalSource === "hc"` is the measured
  // figure itself, and `kcalMethod === "keytel"` is JL's own estimate but
  // computed FROM an HC heart-rate reading (calorieEstimate.ts) — when either
  // is true, `metFallbackKcal` (the caller's MET-only twin of the same
  // estimator) renders in `kcal`'s place; when the caller had no ingredients
  // to produce that twin, the cell is omitted exactly as before this existed.
  const kcalTouchesHealthConnect =
    energy?.kcalSource === "hc" || energy?.kcalMethod === "keytel";
  const kcalForCard = kcalTouchesHealthConnect ? energy?.metFallbackKcal : energy?.kcal;
  if (
    kcalForCard !== undefined &&
    kcalForCard !== null &&
    Number.isFinite(kcalForCard)
  ) {
    cells.push({ label: labels.caloriesLabel, value: formatKcal(kcalForCard) });
  }
  return cells;
};
